import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { ErrorBoundary } from '@dia/ui';
import { useAuth } from './auth';
import { SignIn } from './screens/SignIn';
import { Home } from './screens/Home';
import { Arrive } from './screens/Arrive';
import { HouseGuides } from './screens/HouseGuides';
import { HouseGuide } from './screens/HouseGuide';
import { IslandGuide } from './screens/IslandGuide';
import { Safety } from './screens/Safety';
import { Weather } from './screens/Weather';
import { Requests } from './screens/Requests';
import { Rooms } from './screens/Rooms';
import { Mensajes } from './screens/Mensajes';
import { Traductor } from './screens/Traductor';
import { NotFound } from './screens/NotFound';
import { HelpButton } from './screens/parts/HelpButton';

/**
 * The routing skeleton. Section 6 Phase 1 adds the rest of the screens on top
 * of it: House guides, Island guide, Requests, Mensajes, Traductor, Safety,
 * Weather. Each one is a file in screens/ and a line in this table.
 *
 * Two kinds of route:
 *   public   works before and outside the access window (Arrive, the island),
 *   private  needs a session inside the window (the stay, requests, messages).
 */
export function App() {
  const { session } = useAuth();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteBoundary>
        <Routes>
          <Route path="/entrar" element={session ? <Navigate to="/" replace /> : <SignIn />} />

          {/* Public: a guest planning a trip in March is never locked out of these. */}
          <Route path="/llegada" element={<Arrive />} />
          <Route path="/casas" element={<HouseGuides />} />
          <Route path="/casas/:slug" element={<HouseGuide />} />
          <Route path="/isla" element={<IslandGuide />} />
          <Route path="/seguridad" element={<Safety />} />
          <Route path="/clima" element={<Weather />} />

          {/* Private: a stay session inside the access window. */}
          <Route path="/pedidos" element={session ? <Requests /> : <Navigate to="/entrar" replace />} />
          <Route path="/cuartos" element={session ? <Rooms /> : <Navigate to="/entrar" replace />} />
          <Route path="/mensajes" element={session ? <Mensajes /> : <Navigate to="/entrar" replace />} />
          <Route path="/traductor" element={session ? <Traductor /> : <Navigate to="/entrar" replace />} />

          {/* The tile home (docs/GUEST-REDESIGN.md). It replaced the Your stay list;
              every address above still resolves, so saved links and QR codes keep working. */}
          <Route path="/" element={session ? <Home /> : <Navigate to="/entrar" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Help and safety: the round button on every screen. Inside the same boundary as
            the routes, not a sibling of it: HelpButton reads the session on every single
            screen (useAccess -> accessUiState), so a boundary around Routes alone would
            never see an exception thrown from here, and one nearly did (2026-09-26 audit). */}
        <HelpButton />
      </RouteBoundary>
    </BrowserRouter>
  );
}

/**
 * A screen that throws while drawing shows a message and a retry button rather than a
 * blank page, and moving to another route clears it (resetKey). Ops has had this since
 * its own App.tsx (RouteBoundary there); the guest app never did, so any uncaught
 * exception here white-screened the whole app with nothing on it and no way out except
 * closing the tab. Added as part of the 2026-09-26 audit after finding no other defense
 * exists against a class of crash that includes the one Tim reported.
 */
function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.key}>{children}</ErrorBoundary>;
}

/**
 * A new page opens at its own top, not wherever the last page's scroll
 * happened to be. React Router does not do this on its own (unlike a plain
 * browser navigation), so without it a tile a screen down on the House Guide
 * opened a new, shorter page already scrolled to its bottom (reported
 * 2026-09-26: crew and quiet, how the island works). A link to a spot on the
 * *same* page (a `#id` hash, used by the House Guide's pinned cards) is left
 * alone, so that jump still works.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
